import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { move, nextSeat, startPositions, withPosition, type TrackSpec } from '@campfire/track';

export const CHUTES_LENGTH = 100;
export const LADDERS: Readonly<Record<number, number>> = { 1: 38, 4: 14, 9: 31, 21: 42, 28: 84, 36: 44, 51: 67, 71: 91, 80: 100 };
export const CHUTE_DROPS: Readonly<Record<number, number>> = { 16: 6, 47: 26, 49: 11, 56: 53, 62: 19, 64: 60, 87: 24, 93: 73, 95: 75, 98: 78 };
export const CHUTES_JUMPS: Readonly<Record<number, number>> = { ...LADDERS, ...CHUTE_DROPS };
export const CHUTES_TRACK: TrackSpec = { length: CHUTES_LENGTH, jumps: CHUTES_JUMPS };

export interface ChutesOptions {
  /** Must spin the exact number to land on 100; an overshoot means staying put. */
  readonly exactFinish: boolean;
  /** Spinning a 6 earns another spin. */
  readonly sixAgain: boolean;
}

export interface ChutesEvent {
  readonly seat: Seat;
  readonly spin: number;
  readonly from: number;
  /** Square reached by the spin, before any ladder or chute. */
  readonly landed: number;
  readonly to: number;
  readonly jump: 'ladder' | 'chute' | null;
  /** Spin would have passed 100 (exact finish on): the piece stayed. */
  readonly stayed: boolean;
  readonly again: boolean;
}

export interface ChutesState {
  readonly options: ChutesOptions;
  readonly positions: readonly number[];
  readonly turn: Seat;
  readonly last: ChutesEvent | null;
  readonly result: Outcome | null;
}

export type ChutesAction = { readonly type: 'spin' };

export interface ChutesView {
  readonly you: Seat | null;
  readonly positions: readonly number[];
  readonly turn: Seat;
  readonly last: ChutesEvent | null;
  readonly result: Outcome | null;
  readonly exactFinish: boolean;
  readonly sixAgain: boolean;
  readonly ladders: Readonly<Record<number, number>>;
  readonly chutes: Readonly<Record<number, number>>;
}

export const chutes: GameDefinition<ChutesState, ChutesAction, ChutesView, ChutesOptions> = {
  id: 'chutes',
  name: 'Chutes & Ladders',
  rules: [
    'Race your piece from square 1 to square 100. It is all luck, with no decisions to make.',
    'Everybody starts off the board. On your turn spin the spinner (1 to 6) and your piece moves that many squares.',
    'Land on the bottom of a ladder and you climb to its top. Land on the head of a chute and you slide down to its foot.',
    'Pieces may share a square.',
    'Setting: Reaching 100. By default any spin that gets you to or past 100 wins. Choose Need the exact spin and an overshoot leaves you where you were.',
    'Setting: Spinning a 6. Off by default; choose Spin again and a 6 earns another go, even if you land on a chute.',
    'The ladder at 80 goes to 100, so it wins the game. The app handles all of this; there is nothing for the table to agree on.',
  ],
  minSeats: 2,
  maxSeats: 6,
  defaultOptions: { exactFinish: false, sixAgain: false },
  optionSpecs: [
    {
      key: 'exactFinish',
      label: 'Reaching 100',
      choices: [
        { value: false, label: 'Any spin finishes' },
        { value: true, label: 'Need the exact spin' },
      ],
    },
    {
      key: 'sixAgain',
      label: 'Spinning a 6',
      choices: [
        { value: false, label: 'Nothing special' },
        { value: true, label: 'Spin again' },
      ],
    },
  ],

  setup: ({ seats, options }) => ({ options, positions: startPositions(seats), turn: 0, last: null, result: null }),

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    return a.type === 'spin' ? { ok: true } : { ok: false, reason: 'unknown action' };
  },

  apply(s, _a, { rng }) {
    const seat = s.turn;
    const spin = rng.int(6) + 1;
    const from = s.positions[seat] as number;
    const m = move(CHUTES_TRACK, from, spin, s.options.exactFinish ? 'exact' : 'stop');
    const won = m.finished;
    const again = !won && s.options.sixAgain && spin === 6;
    const jump = m.jump === 'up' ? 'ladder' : m.jump === 'down' ? 'chute' : null;
    const last: ChutesEvent = { seat, spin, from, landed: m.landed, to: m.to, jump, stayed: m.overshot && s.options.exactFinish, again };
    return {
      ...s,
      positions: withPosition(s.positions, seat, m.to),
      turn: won || again ? seat : nextSeat(seat, s.positions.length),
      last,
      result: won ? { winners: [seat], draw: false, reason: `${seat} reached ${CHUTES_LENGTH}` } : null,
    };
  },

  view(s, viewer: Viewer) {
    return {
      you: viewer.kind === 'seat' ? viewer.seat : null,
      positions: s.positions,
      turn: s.turn,
      last: s.last,
      result: s.result,
      exactFinish: s.options.exactFinish,
      sixAgain: s.options.sixAgain,
      ladders: LADDERS,
      chutes: CHUTE_DROPS,
    };
  },

  outcome: (s) => s.result,

  enumerateActions: (s, seat) => (seat === s.turn && !s.result ? [{ type: 'spin' }] : []),
};
